import { _ } from '@sveltia/i18n';

import { isEntryCollection } from '$lib/services/contents/collection';

/**
 * @import { Entry, InternalCollection } from '$lib/types/private';
 * @import { Collection, CollectionIndexFile } from '$lib/types/public';
 */

/**
 * Get the collection’s index file configuration. This function returns the index file configuration
 * if index file inclusion is enabled for the collection. If no specific configuration is provided,
 * it returns a default configuration with the `_index` file name, which is used for Hugo’s special
 * index file.
 * @param {InternalCollection | Collection} collection Collection.
 * @returns {CollectionIndexFile | undefined} Index file configuration if index file inclusion is
 * enabled for the collection, otherwise `undefined`.
 * @see https://gohugo.io/content-management/organization/#index-pages-_indexmd
 * @see https://github.com/decaporg/decap-cms/issues/7381
 * @see https://sveltiacms.app/en/docs/collections/entries#managing-hugo-s-special-index-file
 */
export const getIndexFile = (collection) => {
  if (!isEntryCollection(collection)) {
    return undefined;
  }

  const { index_file: indexFile } = collection;

  if (!indexFile) {
    return undefined;
  }

  const file = indexFile === true ? {} : indexFile;

  return {
    name: file.name ?? '_index',
    label: file.label ?? _('index_file'),
    icon: file.icon ?? 'home',
    // The following properties are inherited from the collection file, collection or global config
    fields: file.fields,
    editor: file.editor,
  };
};

/**
 * Check if index file inclusion (for Hugo) is enabled for the collection, and the given entry is
 * the special index file.
 * @param {InternalCollection} collection Collection.
 * @param {Entry} entry Entry.
 * @returns {boolean} Result.
 */
export const isCollectionIndexFile = (collection, entry) => {
  const indexFile = getIndexFile(collection);

  if (!indexFile) {
    return false;
  }

  return entry.slug === indexFile.name;
};
